import BreadCrumbs from 'components/breadcrumbs'
import LoadingV2 from 'components/LoadingV2'
import PMBPagination from 'components/PMBPagination'
import { TableComponent as DataTable } from 'components/TableComponent'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { ArrowLeft, ChevronDown, RefreshCw } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, Card, CardBody, Spinner } from 'reactstrap'
import { formatDateOnlyPMB, formatDatePMBV2 } from 'utils/formatDate'
import {
  URL_FINDORA_PRODUCT_MAPPING_SCHEMA,
  URL_WORKPLACE,
} from 'utils/pathUrl'
import {
  useSyncFindoraEmployeesMutation,
  useSyncFindoraItemsMutation,
} from '../sync-data/syncDataListApiSlice'
import { useFetchFindoraLogsListQuery } from './findoraLogsListApiSlice'
import { setFetchOption } from './findoraLogsListSlice'

const ListFindoraLogs = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { enqueueSnackbar } = useSnackbar()

  const { tag } = useSelector((state) => state.findoraLogsList)

  const [searchInput, setSearchInput] = useState('')
  const [isFilter, setIsFilter] = useState(false)
  const [isSync, setIsSync] = useState(false)

  const { data, isLoading, refetch, isFetching } = useFetchFindoraLogsListQuery(
    {
      page: tag?.page ?? 0,
      size: tag?.size ?? 10,
      s: tag?.s,
      sortby: tag?.sortby,
      sortdir: tag?.sortdir,
    },
    { refetchOnMountOrArgChange: true, skip: false },
  )

  // NEW COLUMNS
  const columns = [
    {
      name: <span>Tanggal</span>,
      selector: 'createdAt',
      sortable: true,
      grow: 2,
      cell: (row) => (
        <span data-testid={`findora-logs-row-${row.id}`}>
          {row?.createdAt ? formatDatePMBV2(row?.createdAt) : '-'}
        </span>
      ),
      minWidth: '150px',
    },
    {
      name: <span>Jenis</span>,
      selector: 'logName',
      grow: 2,
      cell: (row) => <span className="m-0">{row?.logName ?? ''}</span>,
      minWidth: '150px',
    },
    {
      name: <span>Status</span>,
      selector: 'success',
      minWidth: '150px',
      grow: 2,
      cell: (row) => (
        <div
          className={
            row?.success
              ? 'badge rounded-pill bg-success-50 text-success px-2 flex-centered gap-1'
              : 'badge rounded-pill bg-danger-50 text-danger px-2 flex-centered gap-1'
          }
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            maxWidth: '100%',
          }}
        >
          {row?.success ? 'Berhasil' : 'Gagal'}
        </div>
      ),
    },
    {
      name: <span>Jumlah Karyawan</span>,
      selector: (row) => row?.dump?.numEmployee?.total,
      grow: 2,
      cell: (row) => {
        const data = row?.dump?.numEmployee
        if (!data) return <span className="m-0">-</span>
        const since = data?.since ? formatDateOnlyPMB(data.since) : null
        return (
          <span className="m-0">
            {data?.numCreated ?? 0} data baru{since ? ` sejak ${since}` : ''}{' '}
            (dari {data?.total ?? 0} total)
          </span>
        )
      },
      minWidth: '220px',
    },
    {
      name: <span>Jumlah Hasil Kerja</span>,
      selector: (row) => row?.dump?.numWorkResult?.total,
      grow: 2,
      cell: (row) => {
        const data = row?.dump?.numWorkResult
        if (!data) return <span className="m-0">-</span>
        const since = data?.since ? formatDateOnlyPMB(data.since) : null
        return (
          <span className="m-0">
            {data?.numCreated ?? 0} data baru{since ? ` sejak ${since}` : ''}{' '}
            (dari {data?.total ?? 0} total)
          </span>
        )
      },
      minWidth: '220px',
    },
    {
      name: <span>Jumlah Pembayaran</span>,
      selector: (row) => row?.dump?.numPayments?.total,
      grow: 2,
      cell: (row) => {
        const data = row?.dump?.numPayments
        if (!data) return <span className="m-0">-</span>
        const since = data?.since ? formatDateOnlyPMB(data.since) : null
        return (
          <span className="m-0">
            {data?.numCreated ?? 0} data baru{since ? ` sejak ${since}` : ''}{' '}
            (dari {data?.total ?? 0} total)
          </span>
        )
      },
      minWidth: '220px',
    },
    {
      name: <span>Jumlah Absensi</span>,
      selector: (row) => row?.dump?.numAttendances?.total,
      grow: 2,
      cell: (row) => {
        const data = row?.dump?.numAttendances
        if (!data) return <span className="m-0">-</span>
        const since = data?.since ? formatDateOnlyPMB(data.since) : null
        return (
          <span className="m-0">
            {data?.numCreated ?? 0} data baru{since ? ` sejak ${since}` : ''}{' '}
            (dari {data?.total ?? 0} total)
          </span>
        )
      },
      minWidth: '220px',
    },
    {
      name: <span>Pesan</span>,
      selector: (row) => row?.dump?.message,
      grow: 3,
      cell: (row) => (
        <span
          className={`${row?.success ? 'text-success' : 'text-danger'} m-0`}
        >
          {row?.dump?.message ?? ''}
        </span>
      ),
      minWidth: '200px',
    },
  ]

  const handleSort = (column, direction) => {
    dispatch(
      setFetchOption({
        sortby: column.sortField,
        sortdir: direction?.toUpperCase(),
      }),
    )
  }

  useEffect(() => {
    dispatch(setFetchOption({ page: 0 }))
  }, [searchInput])

  const [syncFindoraItems] = useSyncFindoraItemsMutation()
  const [syncFindoraEmployees] = useSyncFindoraEmployeesMutation()

  const handleSyncData = async () => {
    try {
      setIsSync(true)
      const syncFindoraItemsResponse = await syncFindoraItems()
      const syncFindoraEmployeesResponse = await syncFindoraEmployees()
      // SYNC ITEMS
      if (syncFindoraItemsResponse?.error?.data?.message) {
        throw syncFindoraItemsResponse?.error?.data?.message
      } else {
        enqueueSnackbar('Data item berhasil disinkronisasi', {
          variant: 'success',
        })
      }
      // SYNC EMPLOYEES
      if (syncFindoraEmployeesResponse?.error?.data?.message) {
        throw syncFindoraEmployeesResponse?.error?.data?.message
      } else {
        enqueueSnackbar('Data karyawan berhasil disinkronisasi', {
          variant: 'success',
        })
      }
      dispatch(setFetchOption({ page: 0, s: undefined }))
      setSearchInput('')
      refetch()
    } catch (error) {
      enqueueSnackbar(error || 'Gagal sinkronisasi data', {
        variant: 'error',
      })
      dispatch(setFetchOption({ page: 0, s: undefined }))
      setSearchInput('')
      refetch()
    } finally {
      setIsSync(false)
    }
  }

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      <div className="mt-3">
        <BreadCrumbs
          breadCrumbParent={'Alat Bantu'}
          breadCrumbParentLink={URL_WORKPLACE}
          breadCrumbActive="Daftar Riwayat Sinkronisasi"
        />
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <>
          <div className="row align-items-center py-3 mx-1">
            <div className="col-12 col-md-6 d-flex align-items-center justify-content-start mb-2 mb-md-0">
              <h5 className="fw-semibold mb-0">Daftar Riwayat Sinkronisasi</h5>
            </div>

            <div className="col-12 col-lg-6 d-flex justify-content-md-end gap-2 flex-column flex-md-row">
              <Button
                size="sm"
                outline
                color="primary"
                className="d-flex align-items-center w-md-auto text-nowrap mb-2 mb-md-0 mt-2"
                onClick={() => navigate(URL_FINDORA_PRODUCT_MAPPING_SCHEMA)}
                data-testid="findora-logs-mapping-button"
              >
                <ArrowLeft className="me-1" size={16} />
                Pemetaan Bahan Baku
              </Button>

              <Button
                size="sm"
                color="primary"
                className="d-flex align-items-center w-md-auto text-nowrap mb-2 mb-md-0 mt-2"
                onClick={handleSyncData}
                disabled={isSync}
                data-testid="findora-logs-sync-button"
              >
                {isSync ? (
                  <Spinner size="sm" className="me-2" />
                ) : (
                  <RefreshCw size={16} className="me-2" />
                )}
                Sinkronisasi
              </Button>
            </div>
          </div>
        </>

        <CardBody>
          <DataTable
            responsive
            persistTableHead
            defaultSortFieldId={1}
            defaultSortAsc={false}
            columns={columns}
            data={data?.items || []}
            progressPending={isFetching}
            paginationTotalRows={data?.totalItems}
            pagination
            paginationServer
            paginationComponent={() => (
              <PMBPagination
                rowCount={data?.totalItems || 0}
                currentPage={tag?.page}
                rowsPerPage={tag?.size}
                setCurrentPage={(e) => {
                  dispatch(setFetchOption({ page: e }))
                }}
                setRowsPerPage={(e) => {
                  dispatch(setFetchOption({ size: e }))
                }}
                testId="findora-logs-pagination"
              />
            )}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner />
              </div>
            }
            onSort={handleSort}
            sortIcon={<ChevronDown />}
            sortServer
            noDataComponent={
              <p className="my-3 fw-semibold">
                Belum ada daftar riwayat sinkronisasi findora yang tersedia
              </p>
            }
          />
        </CardBody>
      </Card>
    </div>
  )
}

export default ListFindoraLogs
